import { IconType } from 'react-icons';
import { FaCss3Alt, FaDocker, FaGitAlt, FaJava, FaReact } from 'react-icons/fa';
import {
  SiGradle,
  SiHibernate,
  SiHtml5,
  SiJavascript,
  SiJenkins,
  SiKubernetes,
  SiMongodb,
  SiMysql,
  SiSpringboot,
  SiThymeleaf,
} from 'react-icons/si';
import { FiBox, FiDatabase, FiLayers, FiMonitor } from 'react-icons/fi';
import { motion } from 'framer-motion';
import SectionHeading from './SectionHeading';
import { SiteConfig } from '../types/portfolio';
import Icon from './Icon';

type TechStackProps = {
  techStack: SiteConfig['techStack'];
};

const categoryIcons: Record<string, IconType> = {
  Backend: FiLayers,
  Frontend: FiMonitor,
  Database: FiDatabase,
  DevOps: FiBox,
};

const itemIcons: Record<string, IconType> = {
  Java: FaJava,
  'Spring Boot': SiSpringboot,
  Hibernate: SiHibernate,
  ReactJS: FaReact,
  JavaScript: SiJavascript,
  HTML5: SiHtml5,
  CSS3: FaCss3Alt,
  Thymeleaf: SiThymeleaf,
  MySQL: SiMysql,
  MongoDB: SiMongodb,
  Docker: FaDocker,
  Kubernetes: SiKubernetes,
  Jenkins: SiJenkins,
  Gradle: SiGradle,
  Maven: FaGitAlt,
};

function TechStack({ techStack }: TechStackProps) {
  return (
    <section id="tech-stack" className="section-shell">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          eyebrow="Tech Stack"
          title="A technology toolkit shaped by enterprise delivery."
          description="Badges and categories are organized the way engineering teams discuss systems: backend, frontend, data, and DevOps."
        />

        <div className="grid gap-6 lg:grid-cols-2">
          {techStack.map((group, index) => {
            const CategoryIcon = categoryIcons[group.category] || FiLayers;

            return (
              <motion.article
                key={group.category}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="glass-panel rounded-[2rem] p-8"
              >
                <div className="flex items-center gap-3">
                  <span className="rounded-2xl border border-sky-400/20 bg-sky-400/10 p-3 text-sky-200">
                    <Icon icon={CategoryIcon} />
                  </span>
                  <h3 className="text-xl font-semibold text-white">{group.category}</h3>
                </div>

                <div className="mt-6 grid gap-3 sm:grid-cols-2">
                  {group.items.map((item) => {
                    const ItemIcon = itemIcons[item];

                    return (
                      <div
                        key={item}
                        className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-4 text-sm text-slate-200"
                      >
                        {ItemIcon ? (
                          <Icon icon={ItemIcon} className="text-sky-300" />
                        ) : (
                          <span className="h-2 w-2 rounded-full bg-sky-300" />
                        )}
                        <span>{item}</span>
                      </div>
                    );
                  })}
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default TechStack;
